import { Box, Button, Flex, FlexProps, FormControl, Icon, Link, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Phone, MapPin } from 'lucide-react';
import { useRef } from 'react';
import SectionHeader from './SectionHeader';
import { useLoadEarly } from '../hooks/useLoadEarly';

// Create motion components
const MotionFlex = motion(Flex);
const MotionFormControl = motion(FormControl);

const PHONE_NUMBER = "8327954795";
const ADDRESS_LINES = ["The Lyric Tower", "440 Louisiana Suite 200", "Houston, TX 77002"];
const ADDRESS = ADDRESS_LINES.join(", ");

/**
 * Formats a 10-digit US number as (123) 456-7890.
 */
function formatPhone(phone: string) {
	return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
}

/**
 * Opens the address in the native maps app for the current platform.
 */
function openInMaps(address: string) {
	const query = encodeURIComponent(address);
	const ua = navigator.userAgent;

	if (/Android/.test(ua)) {
		window.location.href = `geo:0,0?q=${query}`;
	} else if (/iPhone|iPad|iPod|Macintosh/.test(ua)) {
		window.open(`https://maps.apple.com/?q=${query}`, "_blank", "noopener,noreferrer");
	} else {
		window.open(
			`https://www.google.com/maps/search/?api=1&query=${query}`,
			"_blank",
			"noopener,noreferrer"
		);
	}
}

/**
 * White call-to-action button that dials the office
 */
export function CallButton() {
	return (
		<Button
			as={"a"}
			href={`tel:+1${PHONE_NUMBER}`}
			height={16}
			paddingX={10}
			fontSize={{ base: "xl", md: "2xl" }}
			fontWeight={700}
			textColor={"blue.100"}
			backgroundColor={"white.100"}
			borderRadius={"full"}
			fontFamily={"Montserrat"}
			boxShadow={"0 8px 24px rgba(0, 0, 0, 0.35)"}
			leftIcon={<Phone size={26} />}
			transition={"transform 0.2s ease, box-shadow 0.2s ease"}
			_hover={{
				transform: "translateY(-2px)",
				boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
				backgroundColor: "white.100",
			}}
			_active={{ transform: "translateY(0)", backgroundColor: "white.200" }}
		>
			Call {formatPhone(PHONE_NUMBER)}
		</Button>
	);
}

/**
 * Office address and a call button, with a map of the office below. Used by the
 * Contact section and at the bottom of each page.
 */
export function ContactInfo(props: FlexProps) {
	const mapRef = useRef<HTMLDivElement>(null);
	const loadMap = useLoadEarly(mapRef);

	return (
		<Flex
			flexDirection={"column"}
			gap={6}
			width={{ base: "90%", md: "80%", lg: "70%" }}
			maxWidth={"1000px"}
			{...props}
		>
			{/* Address + Call */}
			<Flex
				flexDirection={{ base: "column", md: "row" }}
				justifyContent={"space-between"}
				alignItems={{ base: "stretch", md: "center" }}
				gap={5}
			>
				{/* Address (opens the maps app) */}
				<Link
					href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`}
					isExternal
					onClick={(e) => {
						e.preventDefault();
						openInMaps(ADDRESS);
					}}
					display={"flex"}
					alignItems={"flex-start"}
					gap={3}
					color={"white.500"}
					transition={"color 0.2s ease"}
					_hover={{ textDecoration: "none", color: "yellow.100" }}
				>
					<Icon as={MapPin} boxSize={{ base: 7, md: 8 }} color={"yellow.100"} marginTop={0.5} flexShrink={0} />
					<Flex flexDirection={"column"} textAlign={"left"} fontFamily={"Montserrat"} fontSize={{ base: "lg", md: "xl" }} lineHeight={1.4}>
						<Text fontWeight={700} fontSize={{ base: "xl", md: "2xl" }}>
							{ADDRESS_LINES[0]}
						</Text>
						{ADDRESS_LINES.slice(1).map((line) => (
							<Text key={line} fontWeight={500}>
								{line}
							</Text>
						))}
					</Flex>
				</Link>

				{/* Call */}
				<CallButton />
			</Flex>

			{/* Map */}
			<Box
				ref={mapRef}
				height={{ base: "250px", md: "300px" }}
				backgroundColor={"#e5e3df"}
				borderRadius={"xl"}
				overflow={"hidden"}
				boxShadow={"lg"}
			>
				{/* Mounted early so it's loaded before the user scrolls to it */}
				{loadMap && (
					<iframe
						title={"Map of the Outlaw Defense office"}
						src={`https://maps.google.com/maps?q=${encodeURIComponent(ADDRESS)}&z=16&output=embed`}
						width={"100%"}
						height={"100%"}
						style={{ border: 0, display: "block" }}
						referrerPolicy={"no-referrer-when-downgrade"}
						allowFullScreen
					/>
				)}
			</Box>
		</Flex>
	);
}

interface ContactProps {
	// Hide the section header when the page already has its own
	showHeader?: boolean;
}

/**
 * The Contact section
 * @returns The Contact Component
 */
export default function Contact({ showHeader = true }: ContactProps) {

	return (
		<Flex
			id={"contact"}
			paddingTop={showHeader ? 10 : 0}
			width={"100%"}
			flexDirection={"column"}
			alignItems={"center"}
			justifyContent={"center"}
			backgroundColor={"blue.100"}
		>
			{showHeader && (
				<MotionFlex
					width={"100%"}
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.5, ease: "easeOut" }}
				>
					<SectionHeader title={"Contact"} />
				</MotionFlex>
			)}

			{/* Phone, address, and map */}
			<ContactInfo marginTop={showHeader ? 4 : 0} />

			{/* Contact Form */}
			<MotionFormControl
				width={{ base: "90%", md: "60%" }}
				flexDirection={"column"}
				alignItems={"center"}
				padding={"15px"}
				initial={{ opacity: 0 }}
				whileInView={{ opacity: 1 }}
				viewport={{ once: true, amount: 0.2 }}
				transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
			>

			</MotionFormControl>

		</Flex>
	);
}